import type { NextPage, GetStaticPropsContext } from 'next'
import { useRef } from 'react'
import { useTranslations } from 'next-intl'
import useScrollSpy from 'react-use-scrollspy'
import {
  Box,
  Button,
  Center,
  HStack,
  Link,
  Text,
  Image,
  Icon,
  Flex,
  VStack,
  Stack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  useStyleConfig,
} from '@chakra-ui/react'
import SeoHead from '@/components/seo-head'
import NextLink from 'next/link'
import { ContainerBase } from '@/components/containers/ContainerBase'
import { GradientShieldWithArrowIcon } from '@/components/icons/GradientShieldWithArrowIcon'
import { CheckmarkIcon } from '@/components/icons/CheckmarkIcon'

const PhysicalDamageProtectionPage: NextPage = () => {
  const styles = useStyleConfig('Box', { variant: 'white' })
  const t = useTranslations()

  return (
    <>
      <SeoHead
        title={t('damage-protection.seo.title')}
        breadcrumbTitle={t('damage-protection.seo.breadcrumb')}
        description={t('damage-protection.seo.description')}
      />
      <Box __css={styles}>
        <Hero />
        <ContainerBase __css={styles} py={{ base: '12px', md: '40px' }}>
          <ComprehensiveCoverage />
          <HowItWorks />
          <Box mt={'37px'} px={{ base: '21px', md: 'unset' }}>
            <Text
              fontSize={{ base: '22px', md: '22px' }}
              lineHeight={{ base: '30px', md: '30px' }}
            >
              {t('damage-protection.eligible-customers')}
            </Text>
          </Box>
          <Sections />
        </ContainerBase>
      </Box>
    </>
  )
}

const Hero = () => {
  const t = useTranslations()
  return (
    <Box bg="blue.650" position="relative">
      <ContainerBase variant="dark" maxW={{ xl: '1600px' }} p={0}>
        <Box
          width="100%"
          pt={{ base: '50px', md: '56px' }}
          pb={{ base: '20px', md: '26px' }}
          px={{
            base: '10px',
            md: '100px',
            xl: '156px',
          }}
        >
          <Flex
            width="100%"
            height="100%"
            flexDirection="column"
            justifyContent="space-between"
          >
            <VStack spacing="13px" width="100%" height="100%">
              <Icon
                width={{ base: '75px', md: '65px', xl: '75px' }}
                height={'92px'}
                as={GradientShieldWithArrowIcon}
              />
              <Text
                textAlign="center"
                fontWeight="600"
                fontSize={{ base: '24px', md: '32px', xl: '50px' }}
                lineHeight="1.28"
                as="h1"
              >
                {t('damage-protection.seo.title')}
              </Text>
              <Text
                textAlign="center"
                fontWeight="500"
                fontSize={{ base: '16px', md: '18px', xl: '20px' }}
                lineHeight={{ base: '22px', md: '25px', xl: '27px' }}
              >
                {t('damage-protection.description')}
              </Text>
              <NextLink href="#detail-sections" passHref scroll={false}>
                <Link variant="colored" color="white">
                  <Text>{t('damage-protection.learn-more')}</Text>
                </Link>
              </NextLink>
            </VStack>
          </Flex>
        </Box>
      </ContainerBase>
    </Box>
  )
}

const ComprehensiveCoverage = () => {
  const styles = useStyleConfig('Box', { variant: 'gray' })
  const t = useTranslations()
  return (
    <Box
      __css={styles}
      padding={{
        base: '34px 13px',
        md: '62px 100px 77px 100px',
        xl: '72px 250px 87px 121px',
      }}
    >
      <Flex
        width="100%"
        justifyContent={{ base: 'center', md: 'space-between' }}
        flexWrap="wrap"
      >
        <Box width={{ base: '100%', md: '315px' }}>
          <Text
            textAlign={{ base: 'left', md: 'unset' }}
            fontWeight="700"
            fontSize={{ base: '30px', md: '25px', xl: '30px' }}
            lineHeight={{ base: '38px', md: '30px', xl: '36px' }}
            mt={{ base: '0', md: '30px', xl: '40px' }}
          >
            {t('damage-protection.comprehensive-coverage.title')}
          </Text>
          <Text
            textAlign={{ base: 'left', md: 'unset' }}
            fontSize={{ base: '20px', md: '16px', xl: '20px' }}
            lineHeight={{ base: '30px', md: '20px', xl: '24px' }}
            mt={{ base: '15px', md: '11px' }}
          >
            {t('damage-protection.comprehensive-coverage.description')}
          </Text>
        </Box>
        <Image
          src="/img/protection1.png"
          alt="protection1"
          width={{ base: '100%', md: '300px', xl: '323px' }}
        />
      </Flex>
    </Box>
  )
}

const HowItWorks = () => {
  const styles = useStyleConfig('Box', { variant: 'dark' })
  const t = useTranslations()
  return (
    <Box
      __css={styles}
      mt={{ base: '12px', md: '36px' }}
      bg="black.500"
      padding={{
        base: '40px 27px',
        md: '62px 100px 77px 100px',
        xl: '103px 121px',
      }}
    >
      <Flex
        width="100%"
        justifyContent={{ base: 'center', md: 'space-between' }}
        flexWrap="wrap"
      >
        <Box width={{ base: '100%', md: '315px' }}>
          <Text
            textAlign={{ base: 'left', md: 'unset' }}
            fontWeight="700"
            fontSize={{ base: '30px', md: '25px', xl: '30px' }}
            lineHeight={{ base: '38px', md: '30px', xl: '36px' }}
          >
            {t('damage-protection.how-it-works.title')}
          </Text>
          <Text
            textAlign={{ base: 'left', md: 'unset' }}
            color="gray.220"
            fontSize={{ base: '20px', md: '16px', xl: '20px' }}
            lineHeight={{ base: '30px', md: '20px', xl: '24px' }}
            mt={{ base: '15px', md: '11px' }}
            mb={{ base: '30px', md: 'unset' }}
          >
            {t('damage-protection.how-it-works.description')}
          </Text>
        </Box>
        <Image
          height={{ base: '', md: '150px', xl: '100%' }}
          src="/img/protection2.png"
          alt="protection2"
        />
      </Flex>
    </Box>
  )
}

const Sections = () => {
  const t = useTranslations()
  const sections = [
    {
      key: 'plan-details',
      contents: [
        'payment-frequency',
        'contract-length',
        'premium',
        'coverage-limits',
      ],
      nodeContent: null,
    },
    {
      key: 'benefit-details',
      contents: ['replacement-coverage', 'replacement-reimburse', 'volatility'],
      nodeContent: null,
    },
    {
      key: 'whats-covered',
      contents: [],
      nodeContent: <WhatsCoveredTable />,
    },
  ]

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const sectionRefs = sections.map(() => useRef(null))

  const activeSection = useScrollSpy({
    sectionElementRefs: sectionRefs,
    offsetPx: -300,
  })

  return (
    <Box mt={'26px'} px={{ base: '21px', md: 'unset' }}>
      <Flex
        flexDirection={{ base: 'column', md: 'row' }}
        gap={{ md: '80px', xxl: '174px' }}
      >
        <Box
          position="sticky"
          top={{ md: '120px', xxl: '150px' }}
          flexBasis={{ md: '200px', xxl: '274px' }}
          height="500px"
          display={{ base: 'none', md: 'block' }}
        >
          <VStack
            width="100%"
            alignItems="flex-start"
            spacing={{ md: '20px', xxl: '16px' }}
            mt={{ md: '20px', xxl: '15px' }}
          >
            {sections.map((section, i) => {
              return (
                <NextLink
                  key={`${section.key}.section-label`}
                  href={`#${section.key}`}
                  scroll={false}
                  passHref
                >
                  <Link variant="nohover">
                    <Text
                      fontWeight="500"
                      fontSize={{ base: '48px', md: '16px', xxl: '22px' }}
                      lineHeight={{ base: '55px', md: '20px', xxl: '30px' }}
                      borderBottom="4px solid"
                      borderColor={
                        activeSection === i ? 'brand.600' : 'transparent'
                      }
                    >
                      {t(`damage-protection.${section.key}.section-label`)}
                    </Text>
                  </Link>
                </NextLink>
              )
            })}
          </VStack>
        </Box>
        <VStack
          id="detail-sections"
          flex="1"
          alignItems="flex-start"
          width="100%"
          spacing={{ base: '50px', xxl: '100px' }}
          scrollMarginTop={{ base: '280px', lg: '200px' }}
        >
          {sections.map((section, i) => {
            return (
              <VStack
                key={section.key}
                id={section.key}
                alignItems="flex-start"
                ref={sectionRefs[i]}
                width="100%"
                scrollMarginTop={{ base: '58px', lg: '100px' }}
              >
                <Text
                  fontWeight="700"
                  fontSize={{ base: '48px', md: '30px', xxl: '48px' }}
                  lineHeight={{ base: '55px', md: '45px', xxl: '55px' }}
                >
                  {t(`damage-protection.${section.key}.section-label`)}
                </Text>

                {section.nodeContent && section.nodeContent}

                {section.contents.length > 0 && (
                  <VStack
                    pt={{ base: '25px', md: '20px', xl: '46px' }}
                    spacing={{ base: '50px', md: '14px', xl: '53px' }}
                    width="100%"
                  >
                    {section.contents.map((item: string) => {
                      return (
                        <Stack
                          key={item}
                          flexDirection={{ base: 'column', md: 'row' }}
                          alignItems="flex-start"
                          width="100%"
                          spacing={{ base: '10px', md: '0' }}
                        >
                          <HStack flex="58%">
                            <Text
                              fontWeight="600"
                              fontSize={{
                                base: '20px',
                                md: '16px',
                                xl: '20px',
                              }}
                              lineHeight={{
                                base: '27px',
                                md: '20px',
                                xl: '27px',
                              }}
                            >
                              {t(
                                `damage-protection.${section.key}.${item}.label`
                              )}
                            </Text>
                          </HStack>
                          <Text
                            flex="50%"
                            fontWeight="400"
                            fontSize={{ base: '16px', md: '14px', xl: '16px' }}
                            lineHeight={{ base: '20px', md: '19px' }}
                          >
                            {t(
                              `damage-protection.${section.key}.${item}.description`
                            )}
                          </Text>
                        </Stack>
                      )
                    })}
                  </VStack>
                )}
              </VStack>
            )
          })}
        </VStack>
      </Flex>
    </Box>
  )
}

const WhatsCoveredTable = () => {
  const t = useTranslations()

  const rows = [
    {
      label: t('damage-protection.theft'),
      hosting: false,
      compassProtection: true,
    },
    {
      label: t('damage-protection.fire'),
      hosting: false,
      compassProtection: true,
    },
    {
      label: t('damage-protection.flood-water'),
      hosting: false,
      compassProtection: true,
    },
    {
      label: t('damage-protection.weather'),
      hosting: false,
      compassProtection: false,
    },
    {
      label: t('damage-protection.nuclear-war-government'),
      hosting: false,
      compassProtection: false,
    },
  ]

  return (
    <TableContainer width="100%" border="1px solid" borderColor="gray.50">
      <Table variant="striped">
        <Thead>
          <Tr>
            <Th></Th>
            <Th
              textTransform="capitalize"
              fontSize={{ base: '', md: '18px' }}
              lineHeight={{ base: '', md: '1.35' }}
              maxWidth={'200px'}
              whiteSpace="normal"
              textAlign="center"
            >
              <Text variant="gray">
                {t('damage-protection.standard-hosting-plan')}
              </Text>
            </Th>
            <Th
              textTransform="capitalize"
              fontSize={{ base: '', md: '18px' }}
              lineHeight={{ base: '', md: '1.35' }}
              maxWidth={'240px'}
              whiteSpace="normal"
              textAlign="center"
            >
              <Text variant="withDarkMode">
                {t('damage-protection.compass-protection-plan')}
              </Text>
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((row, i) => {
            return (
              <Tr key={`row-${i}`}>
                <Td fontWeight="600" fontSize={{ base: '', md: '20px' }}>
                  {row.label}
                </Td>
                <Td textAlign="center" fontSize={{ base: '', md: '14px' }}>
                  <Text variant="gray">
                    {!row.hosting ? t('damage-protection.not-covered') : '-'}
                  </Text>
                </Td>
                <Td textAlign="center" fontSize={{ base: '', md: '14px' }}>
                  <Text variant="gray">
                    {!row.compassProtection ? (
                      t('damage-protection.not-covered')
                    ) : (
                      <Icon
                        boxSize={'26px'}
                        color="brand.600"
                        as={CheckmarkIcon}
                      />
                    )}
                  </Text>
                </Td>
              </Tr>
            )
          })}
        </Tbody>
      </Table>
    </TableContainer>
  )
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  return {
    props: {
      messages: (await import(`../../locales/${locale}.json`)).default,
    },
  }
}

export default PhysicalDamageProtectionPage
